import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from "react";

type ButtonVariant = "primary" | "ghost" | "gold" | "soft";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  loading?: boolean;
}

// Square buttons, DM Sans uppercase with generous tracking. Per the
// Hamilton design system — no rounded corners, no drop shadows.
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-bordeaux text-rose-paper hover:bg-bordeaux-deep",
  ghost:
    "bg-transparent text-bordeaux border border-bordeaux hover:bg-bordeaux hover:text-rose-paper",
  soft:
    "bg-blush text-bordeaux-deep hover:bg-hot-rose hover:text-rose-paper",
  // Kept for back-compat; champagne is the editorial-gold accent.
  gold:
    "bg-champagne text-bordeaux-deep hover:bg-bordeaux hover:text-rose-paper",
};

const sizeClasses = {
  sm: "px-4 py-2.5 text-[11px]",
  md: "px-[22px] py-[14px] text-[13px]",
  lg: "px-7 py-4 text-sm",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading, className = "", children, disabled, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={[
        "inline-flex items-center justify-center gap-2 font-medium uppercase tracking-[0.18em]",
        "transition-colors duration-200 ease-out cursor-pointer",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variantClasses[variant],
        sizeClasses[size],
        className,
      ].join(" ")}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
});

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className="animate-spin"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
      <path d="M21 12a9 9 0 0 1-9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string | null;
}

// Underline-only inputs, italic-serif placeholders, DM Mono caps for labels.
// Per design system § Form Fields.
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, className = "", id, ...rest },
  ref
) {
  const inputId = id ?? `field-${Math.random().toString(36).slice(2, 9)}`;
  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label
          htmlFor={inputId}
          className="font-mono text-[11px] uppercase tracking-[0.24em] text-ink-mute"
        >
          {label}
        </label>
      ) : null}
      <input
        ref={ref}
        id={inputId}
        className={[
          "w-full bg-transparent text-ink py-2.5",
          "border-0 border-b border-bordeaux focus:border-hot-rose",
          "placeholder:font-serif placeholder:italic placeholder:text-ink-mute",
          "transition-colors duration-200 ease-out focus:outline-none",
          error ? "border-hot-rose" : "",
          className,
        ].join(" ")}
        {...rest}
      />
      {error ? (
        <span className="text-xs text-hot-rose">{error}</span>
      ) : hint ? (
        <span className="text-xs text-ink-mute">{hint}</span>
      ) : null}
    </div>
  );
});

interface CardProps {
  children: ReactNode;
  className?: string;
  /** Adds a hairline accent line at the top */
  accent?: boolean;
}

// Square hairline-bordered card on white. The `accent` prop draws an
// editorial champagne hairline at the top.
export function Card({ children, className = "", accent }: CardProps) {
  return (
    <div
      className={[
        "relative bg-white border border-rule p-7",
        className,
      ].join(" ")}
    >
      {accent ? (
        <div className="absolute top-0 left-7 right-7 h-px bg-champagne/60" />
      ) : null}
      {children}
    </div>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      {eyebrow ? (
        <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-ink-mute">
          {eyebrow}
        </span>
      ) : null}
      <h2 className="font-serif text-3xl text-ink tracking-tight">{title}</h2>
      {description ? (
        <p className="text-[15px] text-ink-mute leading-relaxed max-w-prose">{description}</p>
      ) : null}
    </div>
  );
}

export function Ornament({ className = "" }: { className?: string }) {
  // Editorial hairline divider with a small champagne mark.
  return (
    <div className={["flex items-center justify-center gap-4", className].join(" ")}>
      <span className="h-px w-16 bg-rule" />
      <span className="font-serif italic text-champagne text-base leading-none translate-y-[1px]">·</span>
      <span className="h-px w-16 bg-rule" />
    </div>
  );
}

// Mono caps tag in the pill shape — used for inline metadata stamps.
export function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blush text-bordeaux-deep font-mono text-[10px] uppercase tracking-[0.22em]">
      {children}
    </span>
  );
}

// DM-Mono pill badge. Variants tweak the color block.
export function Badge({ tone = "rose", children }: { tone?: "rose" | "gold" | "plum"; children: ReactNode }) {
  const map = {
    rose: "bg-blush text-bordeaux-deep",
    gold: "bg-champagne/20 text-champagne",
    plum: "bg-bordeaux text-rose-paper",
  };
  return (
    <span
      className={`inline-flex items-center px-3 py-[5px] rounded-full font-mono text-[10px] uppercase tracking-[0.22em] ${map[tone]}`}
    >
      {children}
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      {icon ? <div className="mb-5 text-champagne">{icon}</div> : null}
      <h3 className="font-serif text-2xl text-ink mb-2">{title}</h3>
      {description ? (
        <p className="text-ink-mute max-w-md leading-relaxed mb-6">{description}</p>
      ) : null}
      {action}
    </div>
  );
}
